import { mat3, vec3 } from 'gl-matrix';
import {
    DicomContour,
    ContourGeometricType,
} from '~/domain/dicom/dicomstructureset';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { ContourTexture } from '~/engine/data/structure/ContourTexture';
import { ContourSlice } from '~/engine/data/structure/ContourSlice';
import { Contour } from '~/engine/data/structure/Contour';
import { ContourSequence } from '~/engine/data/structure/ContourSequence';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '~/engine/tool/EventHandlerResult';
import { VolumeViewUpdateEvent } from '~/engine/view/events/VolumeViewUpdateEvent';
import { LayerBit } from '~/engine/view/layers/LayerBit';
import { Orientations } from '~/engine/view/views/PlaneView';
import { ImageObject } from '~/engine/data/image/ImageObject';

export class ContourOperations {
    public getOrientationEnum(orientationMatrix: mat3): Orientations {
        const TRANSVERSAL_MATRIX = mat3.fromValues(1, 0, 0, 0, -1, 0, 0, 0, -1);
        const CORONAL_MATRIX = mat3.fromValues(0, -1, 0, 0, 0, 1, -1, 0, 0);
        const SAGITTAL_MATRIX = mat3.fromValues(1, 0, 0, 0, 0, 1, 0, -1, 0);
        if (mat3.equals(orientationMatrix, TRANSVERSAL_MATRIX)) {
            return Orientations.TRANSVERSAL_ORIENTATION;
        } else if (mat3.equals(orientationMatrix, CORONAL_MATRIX)) {
            return Orientations.CORONAL_ORIENTATION;
        } else if (mat3.equals(orientationMatrix, SAGITTAL_MATRIX)) {
            return Orientations.SAGITTAL_ORIENTATION;
        } else {
            throw new Error('Unknown orientation matrix');
        }
    }
    public polygonToContours(polygonMm: readonly vec3[]): Contour[] {
        const roiContours: DicomContour[] = [];

        const contour = new DicomContour(
            ContourGeometricType.CLOSED_PLANAR,
            polygonMm,
            '',
            ''
        );
        roiContours.push(contour);

        return roiContours.map(
            (contour) => new Contour(contour.data as vec3[])
        );
    }

    public polygonToSdf(
        polygonMm: readonly vec3[],
        targetImage: ImageObject
    ): ContourTexture {
        const contours = this.polygonToContours(polygonMm);

        if (contours.length === 0 || contours[0].vertices.length === 0) {
            return ContourTexture.createEmpty(targetImage.parent.grid);
        }

        const normalDir = targetImage.parent.grid.getUnitZ();
        const normalPosition = vec3.dot(normalDir, contours[0].vertices[0]);

        const contourSequence = new ContourSequence(targetImage.parent.grid, [
            new ContourSlice(normalPosition, contours),
        ]);

        const { texture, renderErrors } = ContourTexture.fromContourSequence(
            targetImage.parent.grid,
            contourSequence
        );

        if (renderErrors) {
            throw new Error(
                `Failed to generate SDF from polygon: ${renderErrors}`
            );
        }

        return texture;
    }

    /*
     *
     * Interpolates the current slice of the SDF texture.
     * @param target - The region of interest to interpolate.
     * @param primaryImage - The image object providing the grid used to snap
     *     the world-space plane coordinate to the visible image slice.
     * @param planeCoordinates - The world-space Z coordinate (mm) of the plane
     *     the user is viewing.
     * @returns The event handler result.
     *
     * */
    public handleInterpolateSingleSlice = (
        target: RegionOfInterest,
        primaryImage: ImageObject,
        planeCoordinates: number
    ): EventHandlerResult => {
        const sourceSdf = target.texture?.signedDistanceField;
        if (!target.texture || !sourceSdf) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED,
                false,
                new VolumeViewUpdateEvent(
                    target.boundingBox,
                    LayerBit.CONTOUR_LAYER_BIT,
                    true
                )
            );
        }
        // Snap to the image slice's center in world space, then floor in
        // texture-voxel space. See Grid.snapPositionToSliceCenter for why
        // floor is correct here.
        const imageGrid = primaryImage.parent.grid;
        const sliceCenterWorld = imageGrid.snapPositionToSliceCenter(
            vec3.fromValues(0, 0, planeCoordinates)
        );
        const curZ = Math.floor(
            target.texture.textureGrid.toVoxelSpace(sliceCenterWorld)[2]
        );
        if (
            !target.texture.textureGrid.includesVoxel(
                vec3.fromValues(0, 0, curZ)
            )
        ) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED,
                false,
                new VolumeViewUpdateEvent(
                    target.boundingBox,
                    LayerBit.CONTOUR_LAYER_BIT,
                    true
                )
            );
        }
        const roiModify = target.startModify('interpolation');
        const totalSlices = sourceSdf.getAllSlices().length;
        const currentSliceVoxels = sourceSdf.sliceToArray(curZ);
        const maxCurrentValue = currentSliceVoxels.reduce(
            (acc, val) => Math.max(acc, val),
            0
        );
        if (maxCurrentValue > 128) {
            roiModify.finish();
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED,
                false,
                new VolumeViewUpdateEvent(
                    target.boundingBox,
                    LayerBit.CONTOUR_LAYER_BIT,
                    true
                )
            );
        }
        let bottomSliceVoxels: Uint8Array | null = null;
        let bottomZ = -1;
        for (let z = curZ - 1; z >= 0; z--) {
            const sliceVoxels = sourceSdf.sliceToArray(z);
            if (sliceVoxels.reduce((acc, val) => Math.max(acc, val), 0) > 128) {
                bottomSliceVoxels = sliceVoxels;
                bottomZ = z;
                break;
            }
        }
        let topSliceVoxels: Uint8Array | null = null;
        let topZ = -1;
        for (let z = curZ + 1; z < totalSlices; z++) {
            const sliceVoxels = sourceSdf.sliceToArray(z);
            if (sliceVoxels.reduce((acc, val) => Math.max(acc, val), 0) > 128) {
                topSliceVoxels = sliceVoxels;
                topZ = z;
                break;
            }
        }
        if (bottomZ === -1 && topZ === -1) {
            roiModify.finish();
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED,
                false,
                new VolumeViewUpdateEvent(
                    target.boundingBox,
                    LayerBit.CONTOUR_LAYER_BIT,
                    true
                )
            );
        }
        bottomSliceVoxels = bottomSliceVoxels ?? currentSliceVoxels;
        topSliceVoxels = topSliceVoxels ?? currentSliceVoxels;
        const interpolatedSliceVoxels = Uint8Array.from(
            currentSliceVoxels.map((_currentValue, i) => {
                const bottomValue = bottomSliceVoxels![i];
                const topValue = topSliceVoxels![i];
                if (bottomZ === -1) {
                    return topValue;
                }
                if (topZ === -1) {
                    return bottomValue;
                }
                const fractionalDistance = (curZ - bottomZ) / (topZ - bottomZ);
                return Math.round(
                    bottomValue + fractionalDistance * (topValue - bottomValue)
                );
            })
        );
        sourceSdf.arrayToTextureSlice(interpolatedSliceVoxels, curZ);
        roiModify.finish();
        return new EventHandlerResult(
            EventHandlerResultType.EVENT_HANDLED,
            true,
            new VolumeViewUpdateEvent(
                target.boundingBox,
                LayerBit.CONTOUR_LAYER_BIT,
                true
            )
        );
    };

    /*
     *
     * Interpolates all empty slices of the SDF texture.
     * @param target - The region of interest to interpolate.
     * @returns The event handler result.
     *
     */
    public handleInterpolateAllSlices = (
        target: RegionOfInterest
    ): EventHandlerResult => {
        const sourceSdf = target.texture?.signedDistanceField;
        if (!sourceSdf) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED,
                false,
                new VolumeViewUpdateEvent(
                    target.boundingBox,
                    LayerBit.CONTOUR_LAYER_BIT,
                    true
                )
            );
        }
        const roiModify = target.startModify('interpolation');
        const totalSlices = sourceSdf.getAllSlices().length;
        for (let curZ = 0; curZ < totalSlices; curZ++) {
            const currentSliceVoxels = sourceSdf.sliceToArray(curZ);
            const maxCurrentValue = currentSliceVoxels.reduce(
                (acc, val) => Math.max(acc, val),
                0
            );
            if (maxCurrentValue > 128) {
                continue;
            }
            let bottomSliceVoxels: Uint8Array | null = null;
            let bottomZ = -1;
            for (let z = curZ - 1; z >= 0; z--) {
                const sliceVoxels = sourceSdf.sliceToArray(z);
                if (
                    sliceVoxels.reduce((acc, val) => Math.max(acc, val), 0) >
                    128
                ) {
                    bottomSliceVoxels = sliceVoxels;
                    bottomZ = z;
                    break;
                }
            }
            let topSliceVoxels: Uint8Array | null = null;
            let topZ = -1;
            for (let z = curZ + 1; z < totalSlices; z++) {
                const sliceVoxels = sourceSdf.sliceToArray(z);
                if (
                    sliceVoxels.reduce((acc, val) => Math.max(acc, val), 0) >
                    128
                ) {
                    topSliceVoxels = sliceVoxels;
                    topZ = z;
                    break;
                }
            }
            if (bottomZ === -1 && topZ === -1) {
                continue;
            }
            bottomSliceVoxels = bottomSliceVoxels ?? currentSliceVoxels;
            topSliceVoxels = topSliceVoxels ?? currentSliceVoxels;
            const interpolatedSliceVoxels = Uint8Array.from(
                currentSliceVoxels.map((currentValue, i) => {
                    const bottomValue = bottomSliceVoxels![i];
                    const topValue = topSliceVoxels![i];
                    if (bottomZ === -1) {
                        return currentValue;
                    }
                    if (topZ === -1) {
                        return currentValue;
                    }
                    const fractionalDistance =
                        (curZ - bottomZ) / (topZ - bottomZ);
                    return Math.round(
                        bottomValue +
                            fractionalDistance * (topValue - bottomValue)
                    );
                })
            );
            sourceSdf.arrayToTextureSlice(interpolatedSliceVoxels, curZ);
        }
        roiModify.finish();
        return new EventHandlerResult(
            EventHandlerResultType.EVENT_HANDLED,
            true,
            new VolumeViewUpdateEvent(
                target.boundingBox,
                LayerBit.CONTOUR_LAYER_BIT
            )
        );
    };
}
